import React, {
  useEffect,
  useState,
} from 'react';
import clsx from 'clsx';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import noop from 'lodash/noop';
import defer from 'lodash/defer';
import keyBy from 'lodash/keyBy';
import isNil from 'lodash/isNil';
import cloneDeep from 'lodash/cloneDeep';
import Controller from 'core/components/FormController';
import Dialog from 'pages/Dashboard/components/Dialog';
import Button from 'pages/Dashboard/components/Button';
import CurrencyTextField from 'core/components/CurrencyTextField';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import NavigationTabs from 'pages/Dashboard/components/NavigationTabs';
import TextArea from 'pages/Dashboard/components/Input/Textarea';
import Input from 'pages/Dashboard/components/Input';
import {
  billingEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useUpsertServices,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  PriceService,
  Price,
} from 'pages/Dashboard/pages/Billing/types';
import {
  useNotify,
} from 'core/hooks';
import ServicesLookup from 'views/EMR/components/ServiceLookup';
import Chip from 'core/components/Chip';
import head from 'lodash/head';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';

export type PriceServiceOption = {
  label: string;
  key: PriceService;
  index: number;
};

export type ChargePolicyDialogParams = {
  price?: Price;
  type?: number;
};

type Props = ChargePolicyDialogParams & {
  onClose: () => void;
};

const defaultPrice: Price = {
  chargeAmount: undefined,
};

const validatePolicyAmount = (value?: unknown) => {
  if (isEmptyString(value)) {
    return false;
  }

  const normalizedValue = typeof value === 'string'
    ? Number(value.replace(/[^0-9.-]/g, ''))
    : Number(value);

  return Number.isFinite(normalizedValue) && normalizedValue >= 0;
};

function getCurrencyValue(value: unknown) {
  return typeof value === 'number' || typeof value === 'string' || isNil(value)
    ? value ?? null
    : null;
}

type TitleProps = {
  isEdit?: boolean;
  active: number;
  onChange: (index: number) => void;
};

export const priceServiceOptions: PriceServiceOption[] = [
  { label: 'CPT Code', key: 'cpt', index: 0 },
  { label: 'Custom Service', key: 'custom', index: 1 },
];

export const priceServiceOptionsByType = keyBy(priceServiceOptions, 'key');

function Title({
  active,
  onChange,
  isEdit,
}: TitleProps) {
  return (
    <div className="flex flex-1 flex-col space-y-2">
      <h2 className="text-lg font-semibold">
        {isEdit ? 'Edit Charge Policy' : 'Create Charge Policy'}
      </h2>
      {!isEdit && (
        <NavigationTabs
          className="flex"
          tabs={priceServiceOptions}
          activeIndex={active}
          onClick={onChange}
        />
      )}
    </div>
  );
}

export default function ChargePolicyDialog({
  price,
  type: defaultServiceType = 0,
  onClose,
}: Props) {
  const [serviceType, setServiceType] = useState<number>(defaultServiceType);
  const notify = useNotify();
  const isCPTCode = serviceType === 0;
  const isEdit = !isNil(price);
  const { mutateAsync: upsertChargePolicy } = useUpsertServices();

  const formProps = useForm<Price>({
    mode: 'onChange',
    defaultValues: price ?? cloneDeep(defaultPrice),
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    resetField,
    formState: { isValid, isSubmitting, defaultValues },
    reset,
  } = formProps;

  const billingCode = watch('billingCode');

  const onSubmit = async (data: Price) => {
    try {
      await upsertChargePolicy({
        ...data,
        isCustom: serviceType === 1,
      });
      notify({
        message: 'Charge policy added succesfully',
        variant: 'success',
      });
      reset();
      onClose();
      billingEventChannel.emit('onPricesChanged');
    } finally {
      reset(data, { keepIsValid: true });
    }
  };

  useEffect(() => {
    reset(defaultValues);
    defer(() => {
      resetField('chargeAmount');
    });
  }, [serviceType]);

  return (
    <Dialog
      title={(
        <Title
          active={serviceType}
          onChange={setServiceType}
          isEdit={isEdit}
        />
      )}
      closeBtnClasses="!-top-1"
      onClose={onClose}
      onConfirm={noop}
      className="!w-[28rem]"
      maxWidth="lg"
      open
    >
      <FormProvider {...formProps}>
        <div className="flex flex-col gap-form-row">
          {isCPTCode && (
          <>
            {!isEdit && (
            <Controller
              name="billingCode"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange, ...field } }) => (
                <ServicesLookup
                  {...field}
                  type="CPTCode"
                  label="Billing Codes"
                  placeholder="Search Billing Code"
                  dropdownId="cpt-code-selection"
                  id="billingCodes"
                  value={[]}
                  normalizeOption={(option) => option}
                  onChange={(value) => {
                    onChange(head(value));
                  }}
                  classes={{
                    root: 'w-full',
                    label: 'text-xs text-gray-500',
                  }}
                />
              )}
            />
            )}
            {!isNil(billingCode) && (
            <Chip
              value={`${billingCode.code} - ${billingCode.description}`}
              onClose={() => { setValue('billingCode', undefined, { shouldValidate: true }); }}
              disabled={isEdit}
              className="w-full truncate h-8"
              classes={{
                chip: 'w-full h-full',
                text: '!text-xs !max-w-[20rem]',
              }}
            />
            )}
          </>
          )}
          {!isCPTCode && (
          <>
            <Controller
              name="customServiceCode.serviceName"
              control={control}
              rules={{ required: true, validate: notEmptyString }}
              render={({ field }) => (
                <Input
                  {...field}
                  className={clsx('text-gray-500 text-xs', {
                    '!mt-0': !isEdit,
                  })}
                  id="custom-service-name"
                  label="Service"
                  required
                  inputProps={{ maxLength: 64 }}
                />
              )}
            />
            <Controller
              name="customServiceCode.description"
              control={control}
              render={({ field }) => (
                <Wrapper
                  id="price-description-wrapper"
                  label="Description"
                  className="text-gray-500 text-xs"
                >
                  <TextArea
                    {...field}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    className="h-full w-full text-gray-900"
                    id="prices-description"
                    placeholder="Add Description"
                    rows={4}
                  />
                </Wrapper>
              )}
            />
          </>
          )}
          <Controller
            name="chargeAmount"
            control={control}
            rules={{ required: true, validate: validatePolicyAmount }}
            render={
            ({ field, fieldState: { error } }) => (
              <Wrapper
                id="price"
                labelClasses="font-semibold text-xs text-gray-500"
                label="Price"
                showErrorText={false}
                error={!isNil(error)}
                className="!space-y-1 !w-full"
                required
              >
                <CurrencyTextField
                  value={getCurrencyValue(field.value)}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  defaultValue=""
                />
              </Wrapper>
            )
          }
          />
          <div className="flex justify-end items-center space-x-2">
            <Button
              variant="outlined"
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              onClick={handleSubmit(onSubmit)}
              disabled={!isValid || isSubmitting}
            >
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </FormProvider>
    </Dialog>
  );
}
